import { useEffect, useState } from 'react';
import { ContentLayout } from '../../Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useInterval } from '@/hooks/useInterval';
import { PageColumn } from '@/components/Page/PageColumn/PageColumn';
import { PageContent } from '@/components/Page/PageContent/PageContent';
import { WIRE_STATUS_DB } from '@/const/wire';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isPaymentDetailsProvidedWireStatus, isRetryWireStatus } from '@/helpers/wireHelpers';
import { AdBanner } from '@/bundle/shared/components/banner/AdBanner/AdBanner';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { useHasAnyOrganizationAdminRole } from '@/bundle/shared/hooks/useHasAnyOrganizationAdminRole';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { RecipientPaymentDetails } from './ui/RecipientPaymentDetails/RecipientPaymentDetails';
import { RecipientProvidePaymentDetails } from './ui/RecipientProvidePaymentDetails/RecipientProvidePaymentDetails';
import { ProxyEditRecipientForm } from './ui/ProxyEditRecipientForm/ProxyEditRecipientForm';
import { RecipientGeneralDetails } from './ui/RecipientGeneralDetails/RecipientGeneralDetails';
import { useLoadTrustStampStatus } from './queries/useLoadTrustStampStatus';
import { getIsEnabledTrustStampDetailsRequest } from './ui/RecipientDocumentVerification/helpers';
import { RedirectButton } from '@/components/RedirectButton/RedirectButton';
import { TrustStampResponseStatusType } from '@/api/v1/recipient/getRecipientTrustStampStatusApi';
import { FormattedResponseType } from '@/helpers/apiHelpers/responseHelpers';
import { getRecipientWiresPath } from '../path/path';
import { getRecipientWireDetailsPath } from './path/path';
import { loadRecipientWire } from '@/bundle/_Recipient/_Details/api/api';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const RecipientWireDetailsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { scrollToTop } = useScrollToTop();
  const { hasAnyOrganizationAdminRole } = useHasAnyOrganizationAdminRole();
  const location = useLocation();
  const { navigateBack } = useStatefulNavigation();

  const [isEditRecipient, setIsEditRecipient] = useState(false);

  const { data: recipientWireData } = useQuery({
    queryKey: ['load_recipient_wire', id],
    queryFn: () => loadRecipientWire(id),
  });

  const recipientWire = recipientWireData?.body;

  const {
    data: trustStampStatusData,
    isPending: isPendingTrustStampData,
    refetch,
  } = useLoadTrustStampStatus(id, recipientWire);

  const wireStatus = recipientWire?.status;
  const isPaymentDetailsProvided = isPaymentDetailsProvidedWireStatus(wireStatus);

  const { cancelInterval, isIntervalRunning } = useInterval(() => {
    if (wireStatus === WIRE_STATUS_DB.PENDING) {
      queryClient.invalidateQueries({ queryKey: ['load_recipient_wire', id] });
    }
  });

  // Cancel polling logic
  useEffect(() => {
    const shouldCancelInterval = wireStatus && wireStatus !== WIRE_STATUS_DB.PENDING && isIntervalRunning;

    if (shouldCancelInterval) {
      cancelInterval();
    }
  }, [cancelInterval, isIntervalRunning, wireStatus]);

  useEffect(() => {
    if (isPaymentDetailsProvided) {
      scrollToTop();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentDetailsProvided]);

  const navigateToWires = () => {
    navigateBack(getRecipientWiresPath());
  };

  const refetchRecipientWire = () => {
    queryClient.invalidateQueries({ queryKey: ['load_recipient_wire', id] });
  };

  const closeEditRecipientForm = () => {
    setIsEditRecipient(false);
  };

  const proxyEditRecipientSuccess = () => {
    refetchRecipientWire();
    closeEditRecipientForm();
  };

  const proxyEditRecipientOpenEdit = () => {
    setIsEditRecipient(true);
    amplitudeService.logEvent(AMPLITUDE_EVENTS.OutboundWireProxyEditRecipientRedirect);
  };

  const resetTrustStampNextAttemptTimer = () => {
    queryClient.setQueryData(
      ['load_trust_stamp_status', id],
      (previousTrustStampData: FormattedResponseType<TrustStampResponseStatusType>) => {
        if (previousTrustStampData?.body?.start_new_session_enable_after_sec === 0) return;

        return {
          ...previousTrustStampData?.body,
          start_new_session_enable_after_sec: 0,
        };
      }
    );

    queryClient.invalidateQueries({ queryKey: ['load_trust_stamp_status', id] });
  };

  const resetProvidePaymentDetailsTimer = () => {
    queryClient.invalidateQueries({ queryKey: ['load_trust_stamp_status', id] });
  };

  const startTrustStampVerification = () => {
    refetch();
  };

  const showRecipientPaymentDetails = !!recipientWire?.is_payment_details_exist || isRetryWireStatus(wireStatus);
  const redirectToWire = () => {
    navigate(getRecipientWireDetailsPath(location.state?.id));
  };

  const hasRedirectButton = location?.state?.id;
  const trustStampDetails = trustStampStatusData?.body;

  return (
    <ContentLayout
      title='Wire Details'
      breadcrumbs='Wires > Details'
      backButton='Back to Wires'
      onBack={navigateToWires}
      banner={isPaymentDetailsProvided && !hasAnyOrganizationAdminRole && <AdBanner />}
    >
      <PageBody>
        <PageContent>
          <PageColumn>
            <RecipientGeneralDetails
              wire={recipientWire}
              trustStampDetails={trustStampDetails}
              isEditRecipient={isEditRecipient}
              onProxyEditRecipientOpenEdit={proxyEditRecipientOpenEdit}
            />
          </PageColumn>
          <PageColumn>
            {showRecipientPaymentDetails && <RecipientPaymentDetails wire={recipientWire} />}
            <RecipientProvidePaymentDetails
              wire={recipientWire}
              isLoadingTrustStampDetails={
                getIsEnabledTrustStampDetailsRequest(recipientWire) && isPendingTrustStampData
              }
              trustStampDetails={trustStampDetails}
              onRefetchWire={refetchRecipientWire}
              onStartTrustStamp={startTrustStampVerification}
              onResetTrustStampNextAttemptTimer={resetTrustStampNextAttemptTimer}
              onResetProvidePaymentDetailsTimer={resetProvidePaymentDetailsTimer}
            />

            {isEditRecipient && (
              <ProxyEditRecipientForm
                wire={recipientWire}
                onClose={closeEditRecipientForm}
                onSave={proxyEditRecipientSuccess}
              />
            )}
          </PageColumn>
        </PageContent>
      </PageBody>
      {hasRedirectButton && <RedirectButton onNavigate={redirectToWire} />}
    </ContentLayout>
  );
};
